import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Briefcase, Leaf, Brain, Award, Mail, Phone, CheckCircle, 
  Download, Menu, X, Eye, Trash2, RefreshCw, ArrowLeft,
  BarChart3, Clock, MessageSquare, Archive
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== NAVIGATION ====================
const Navigation = ({ isAdmin = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#021A2B]/95 backdrop-blur-sm border-b border-[#00CED1]/20">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img 
            src="https://project-maestro-13.preview.static.emergentagent.com/melp-logo.png" 
            alt="MELP Core" 
            className="h-10 w-auto"
          />
          <div>
            <div className="text-white font-medium">MELP CORE LIMITED</div>
            <div className="text-[#00CED1] text-xs">CEO: Mary Chapman</div>
          </div>
        </Link>
        
        {!isAdmin ? (
          <>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-[#00CED1] transition-colors text-sm">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-[#00CED1] transition-colors text-sm">Services</button>
              <button onClick={() => scrollToSection('projects')} className="text-gray-300 hover:text-[#00CED1] transition-colors text-sm">Projects</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-[#00CED1] transition-colors text-sm">Contact</button>
              <a 
                href="/Mary_Chapman_CV.pdf" 
                download
                className="flex items-center gap-2 px-4 py-2 border border-[#00CED1] text-[#00CED1] rounded hover:bg-[#00CED1]/10 transition-colors text-sm"
              >
                <Download className="h-4 w-4" />
                Download CV
              </a>
            </div>
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </>
        ) : (
          <Link to="/" className="flex items-center gap-2 text-[#00CED1] hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Site
          </Link>
        )}
      </div>
      
      {mobileMenuOpen && !isAdmin && (
        <div className="md:hidden bg-[#021A2B] border-t border-[#00CED1]/20 px-6 py-4 space-y-4">
          <button onClick={() => scrollToSection('about')} className="block text-gray-300 hover:text-[#00CED1] transition-colors">About</button>
          <button onClick={() => scrollToSection('services')} className="block text-gray-300 hover:text-[#00CED1] transition-colors">Services</button>
          <button onClick={() => scrollToSection('projects')} className="block text-gray-300 hover:text-[#00CED1] transition-colors">Projects</button>
          <button onClick={() => scrollToSection('contact')} className="block text-gray-300 hover:text-[#00CED1] transition-colors">Contact</button>
        </div>
      )}
    </nav>
  );
};

// ==================== HERO SECTION ====================
const HeroSection = () => (
  <section className="min-h-screen flex items-center justify-center px-6 pt-24 relative overflow-hidden">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-20 right-20 w-64 h-64 border-2 border-[#00CED1] rotate-45"></div>
      <div className="absolute bottom-20 left-20 w-48 h-48 border-2 border-[#00CED1] rotate-12"></div>
      <div className="absolute top-1/2 left-1/4 w-32 h-32 border-2 border-[#00CED1]"></div>
    </div>
    <div className="max-w-5xl mx-auto text-center relative z-10">
      <div className="mb-8">
        <div className="w-32 h-32 mx-auto mb-8 relative">
          <div className="absolute inset-0 border-2 border-[#00CED1] rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
          <div className="absolute inset-4 border-2 border-[#00CED1]/50 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
          <div className="absolute inset-8 border-2 border-[#00CED1] rounded-full animate-spin" style={{ animationDuration: '10s' }}></div>
        </div>
      </div>
      <h1 className="text-5xl md:text-7xl lg:text-8xl font-light tracking-tight mb-6" style={{ letterSpacing: '-0.02em' }}>
        MELP CORE LIMITED
      </h1>
      <p className="text-lg md:text-xl text-[#00CED1] mb-4 font-light">CEO: Mary Chapman</p>
      <p className="text-base md:text-lg text-gray-400 mb-8">MSc. Industrial Engineering | MSc. Sustainable Engineering</p>
      <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
        Delivering complex programs with clarity, confidence and regulatory integrity
      </p>
    </div>
  </section>
);

// ==================== ABOUT SECTION ====================
const AboutSection = () => (
  <section id="about" className="py-24 px-6 bg-[#052B3F]">
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-12">
        <img 
          alt="MELP Core" 
          className="h-20 w-auto" 
          src="https://project-maestro-13.preview.static.emergentagent.com/melp-logo.png"
        />
      </div>
      <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-12 tracking-tight text-[#00CED1]">
        MELP CORE LIMITED
      </h2>
      <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
        A specialist consultancy operating at the intersection of project management, artificial intelligence and sustainable Engineering within the pharmaceutical and semiconductor industries. We help organisations deliver complex programs with clarity, confidence and regulatory integrity, while embedding sustainability into decision-making - not as an afterthought, but as a core performance driver.
      </p>
    </div>
  </section>
);

// ==================== SERVICES SECTION ====================
const ServicesSection = () => {
  const services = [
    {
      icon: Briefcase,
      title: "Project Management",
      description: "Expert delivery of complex programs in pharmaceutical and semiconductor industries with regulatory integrity and strategic clarity."
    },
    {
      icon: Leaf,
      title: "Sustainable Engineering",
      description: "Embedding sustainability into core decision-making processes, driving measurable environmental impact and operational excellence."
    },
    {
      icon: Brain,
      title: "Artificial Intelligence",
      description: "Leveraging AI technologies to optimize operations, enhance decision-making, and drive innovation in complex industrial environments."
    }
  ];

  return (
    <section id="services" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-16 tracking-tight text-[#00CED1]">Services</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div key={index} className="bg-[#052B3F] border-2 border-[#00CED1]/30 rounded-xl p-6 hover:border-[#00CED1] hover:shadow-lg hover:shadow-[#00CED1]/20 transition-all duration-300">
              <service.icon className="h-10 w-10 text-[#00CED1] mb-4" />
              <h3 className="text-2xl font-light text-white mb-4">{service.title}</h3>
              <p className="text-gray-300">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== PROJECTS SECTION ====================
const ProjectsSection = () => {
  const projects = [
    {
      industry: "Biopharmaceutical",
      title: "Global Packaging Transformation",
      role: "Senior Project Manager",
      description: "Comprehensive packaging process redesign for global biopharmaceutical operations, achieving significant environmental and operational improvements.",
      impact: "160 tonnes of plastic saved per year",
      metrics: [
        { label: "Plastic Reduction", value: "160 tonnes/year" },
        { label: "Industry", value: "Biopharmaceutical" },
        { label: "Scope", value: "Global" }
      ]
    },
    {
      industry: "Semiconductor",
      title: "Industrial Efficiency Program",
      role: "Senior Project Manager",
      description: "Strategic laptop recycling and efficiency initiative for global semiconductor manufacturer, delivering substantial cost savings and environmental benefits.",
      impact: "$100,000 annual cost savings",
      metrics: [
        { label: "Cost Savings", value: "$100,000/year" },
        { label: "Industry", value: "Semiconductor" },
        { label: "Scope", value: "Global" }
      ]
    }
  ];

  return (
    <section id="projects" className="py-24 px-6 bg-[#052B3F]">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-16 tracking-tight text-[#00CED1]">Featured Projects</h2>
        <div className="grid md:grid-cols-2 gap-12">
          {projects.map((project, index) => (
            <div key={index} className="bg-[#021A2B] border-2 border-[#00CED1]/30 rounded-xl p-6 hover:border-[#00CED1] hover:shadow-lg hover:shadow-[#00CED1]/20 transition-all duration-300">
              <div className="text-sm text-[#00CED1] mb-2">{project.industry}</div>
              <h3 className="text-2xl md:text-3xl font-light mb-2 text-white">{project.title}</h3>
              <div className="text-sm text-gray-400 mb-4">{project.role}</div>
              <p className="text-gray-300 mb-6">{project.description}</p>
              <div className="bg-[#00CED1] text-[#021A2B] p-4 mb-6 rounded">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-light">Impact:</span>
                </div>
                <p className="text-lg font-light">{project.impact}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {project.metrics.map((metric, idx) => (
                  <div key={idx}>
                    <div className="text-xs text-gray-500 mb-1">{metric.label}</div>
                    <div className="text-sm font-light text-[#00CED1]">{metric.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== QUALIFICATIONS SECTION ====================
const QualificationsSection = () => {
  const qualifications = [
    { type: "Education", title: "MSc. Industrial Engineering" },
    { type: "Education", title: "MSc. Sustainable Engineering" },
    { type: "Certification", title: "PMP Professional", subtitle: "Institute of Project Management Ireland" }
  ];

  return (
    <section className="py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-16 tracking-tight text-[#00CED1]">Qualifications</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {qualifications.map((qual, index) => (
            <div key={index} className="bg-[#052B3F] border-2 border-[#00CED1]/30 p-6 hover:border-[#00CED1] hover:shadow-lg hover:shadow-[#00CED1]/20 transition-all duration-300">
              <Award className="h-8 w-8 mb-4 text-[#00CED1]" />
              <div className="text-xs text-gray-500 mb-2">{qual.type}</div>
              <h3 className="text-xl font-light mb-2 text-white">{qual.title}</h3>
              {qual.subtitle && <p className="text-sm text-gray-400">{qual.subtitle}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== CONTACT SECTION ====================
const ContactSection = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    project_description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! Your inquiry has been submitted. We\'ll be in touch soon.');
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        project_description: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      const message = error.response?.data?.detail || 'Failed to submit. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 px-6 bg-[#052B3F]">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-12 tracking-tight text-[#00CED1]">Start a Project</h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <p className="text-lg font-light mb-8 text-gray-300">
              Ready to deliver your next complex program with clarity and confidence? Let's discuss how we can help.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#00CED1]" />
                <a href="mailto:mcclmary@gmail.com" className="hover:text-[#00CED1] transition-colors">mcclmary@gmail.com</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#00CED1]" />
                <a href="tel:+353897007498" className="hover:text-[#00CED1] transition-colors">+353897007498</a>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <label className="text-sm font-medium text-white" htmlFor="company_name">Company Name</label>
              <input
                className="mt-2 flex h-10 w-full rounded-md border bg-[#021A2B] border-[#00CED1]/30 text-white px-3 py-2 text-sm focus:border-[#00CED1] focus:outline-none"
                id="company_name"
                name="company_name"
                required
                value={formData.company_name}
                onChange={handleChange}
                data-testid="company-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white" htmlFor="contact_name">Contact Name</label>
              <input
                className="mt-2 flex h-10 w-full rounded-md border bg-[#021A2B] border-[#00CED1]/30 text-white px-3 py-2 text-sm focus:border-[#00CED1] focus:outline-none"
                id="contact_name"
                name="contact_name"
                required
                value={formData.contact_name}
                onChange={handleChange}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white" htmlFor="email">Email</label>
              <input
                className="mt-2 flex h-10 w-full rounded-md border bg-[#021A2B] border-[#00CED1]/30 text-white px-3 py-2 text-sm focus:border-[#00CED1] focus:outline-none"
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white" htmlFor="phone">Phone</label>
              <input
                className="mt-2 flex h-10 w-full rounded-md border bg-[#021A2B] border-[#00CED1]/30 text-white px-3 py-2 text-sm focus:border-[#00CED1] focus:outline-none"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                data-testid="phone-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white" htmlFor="project_description">Project Description</label>
              <textarea
                className="mt-2 flex min-h-[120px] w-full rounded-md border bg-[#021A2B] border-[#00CED1]/30 text-white px-3 py-2 text-sm focus:border-[#00CED1] focus:outline-none resize-none"
                id="project_description"
                name="project_description"
                required
                rows={4}
                value={formData.project_description}
                onChange={handleChange}
                data-testid="project-description-input"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#00CED1] text-[#021A2B] hover:bg-[#00F5D4] h-10 px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-contact-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// ==================== FOOTER ====================
const Footer = () => (
  <footer className="py-8 px-6 bg-[#021A2B] border-t border-[#00CED1]/20">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img 
          src="https://project-maestro-13.preview.static.emergentagent.com/melp-logo.png" 
          alt="MELP Core" 
          className="h-10 w-auto"
        />
        <span className="text-gray-400 text-sm">© 2025 MELP CORE LIMITED. All rights reserved.</span>
      </div>
      <a 
        href="/Mary_Chapman_CV.pdf" 
        download
        className="flex items-center gap-2 px-4 py-2 border border-[#00CED1] text-[#00CED1] rounded hover:bg-[#00CED1]/10 transition-colors text-sm"
      >
        <Download className="h-4 w-4" />
        Download CV
      </a>
    </div>
  </footer>
);

// ==================== MAIN PORTFOLIO PAGE ====================
const PortfolioPage = () => (
  <div className="min-h-screen bg-[#021A2B] text-white">
    <Navigation />
    <HeroSection />
    <AboutSection />
    <ServicesSection />
    <ProjectsSection />
    <QualificationsSection />
    <ContactSection />
    <Footer />
  </div>
);

// ==================== ADMIN DASHBOARD ====================
const AdminDashboard = () => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, responded: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchContacts = async () => {
    try {
      const url = statusFilter ? `${API}/contact?status=${statusFilter}` : `${API}/contact`;
      const response = await axios.get(url);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/contact/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchContacts(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status });
      toast.success(`Status updated to ${status}`);
      fetchContacts();
      fetchStats();
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    try {
      await axios.delete(`${API}/contact/${id}`);
      toast.success('Inquiry deleted');
      fetchContacts();
      fetchStats();
      if (selectedContact?.id === id) {
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete inquiry');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'read': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'responded': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'archived': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#021A2B] text-white">
      <Navigation isAdmin />
      
      <div className="pt-24 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl md:text-4xl font-light text-[#00CED1]" data-testid="admin-title">Project Inquiries</h1>
            <button 
              onClick={() => { fetchContacts(); fetchStats(); }}
              className="flex items-center gap-2 px-4 py-2 bg-[#052B3F] border border-[#00CED1]/30 rounded hover:border-[#00CED1] transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="stats-cards">
            <div className="bg-[#052B3F] border border-[#00CED1]/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs">Total</span>
              </div>
              <div className="text-2xl font-light text-white">{stats.total}</div>
            </div>
            <div className="bg-[#052B3F] border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-400 mb-2">
                <Clock className="h-4 w-4" />
                <span className="text-xs">New</span>
              </div>
              <div className="text-2xl font-light text-green-400">{stats.new}</div>
            </div>
            <div className="bg-[#052B3F] border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <Eye className="h-4 w-4" />
                <span className="text-xs">Read</span>
              </div>
              <div className="text-2xl font-light text-blue-400">{stats.read}</div>
            </div>
            <div className="bg-[#052B3F] border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-purple-400 mb-2">
                <MessageSquare className="h-4 w-4" />
                <span className="text-xs">Responded</span>
              </div>
              <div className="text-2xl font-light text-purple-400">{stats.responded}</div>
            </div>
            <div className="bg-[#052B3F] border border-gray-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <Archive className="h-4 w-4" />
                <span className="text-xs">Archived</span>
              </div>
              <div className="text-2xl font-light text-gray-400">{stats.archived}</div>
            </div>
          </div>

          {/* Filter */}
          <div className="mb-6">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#052B3F] border border-[#00CED1]/30 rounded px-4 py-2 text-white focus:border-[#00CED1] focus:outline-none"
              data-testid="status-filter"
            >
              <option value="">All Inquiries</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="responded">Responded</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12 text-gray-400" data-testid="no-contacts">
              No inquiries found. New submissions will appear here.
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Contact List */}
              <div className="lg:col-span-1 space-y-4" data-testid="contact-list">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => { setSelectedContact(contact); if (contact.status === 'new') updateStatus(contact.id, 'read'); }}
                    className={`bg-[#052B3F] border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedContact?.id === contact.id 
                        ? 'border-[#00CED1] shadow-lg shadow-[#00CED1]/20' 
                        : 'border-[#00CED1]/30 hover:border-[#00CED1]/60'
                    }`}
                    data-testid={`contact-item-${contact.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-white truncate">{contact.company_name}</h3>
                      <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(contact.status)}`}>
                        {contact.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 truncate">{contact.contact_name}</p>
                    <p className="text-xs text-gray-500 mt-2">{formatDate(contact.created_at)}</p>
                  </div>
                ))}
              </div>

              {/* Contact Detail */}
              <div className="lg:col-span-2" data-testid="contact-detail">
                {selectedContact ? (
                  <div className="bg-[#052B3F] border border-[#00CED1]/30 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-light text-white mb-1">{selectedContact.company_name}</h2>
                        <p className="text-gray-400">{selectedContact.contact_name}</p>
                      </div>
                      <span className={`text-sm px-3 py-1 rounded border ${getStatusColor(selectedContact.status)}`}>
                        {selectedContact.status}
                      </span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="text-xs text-gray-500">Email</label>
                        <a href={`mailto:${selectedContact.email}`} className="block text-[#00CED1] hover:underline">
                          {selectedContact.email}
                        </a>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Phone</label>
                        <a href={`tel:${selectedContact.phone}`} className="block text-[#00CED1] hover:underline">
                          {selectedContact.phone || 'Not provided'}
                        </a>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="text-xs text-gray-500">Project Description</label>
                      <p className="text-gray-300 mt-1 whitespace-pre-wrap">{selectedContact.project_description}</p>
                    </div>

                    <div className="mb-6">
                      <label className="text-xs text-gray-500">Received</label>
                      <p className="text-gray-400">{formatDate(selectedContact.created_at)}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-[#00CED1]/20">
                      <button
                        onClick={() => updateStatus(selectedContact.id, 'responded')}
                        className="px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded hover:bg-purple-500/30 transition-colors"
                        data-testid="mark-responded-btn"
                      >
                        Mark Responded
                      </button>
                      <button
                        onClick={() => updateStatus(selectedContact.id, 'archived')}
                        className="px-4 py-2 bg-gray-500/20 text-gray-400 border border-gray-500/30 rounded hover:bg-gray-500/30 transition-colors"
                        data-testid="archive-btn"
                      >
                        Archive
                      </button>
                      <button
                        onClick={() => deleteContact(selectedContact.id)}
                        className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded hover:bg-red-500/30 transition-colors ml-auto"
                        data-testid="delete-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#052B3F] border border-[#00CED1]/30 rounded-lg p-12 text-center text-gray-400">
                    Select an inquiry to view details
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== APP ====================
function App() {
  return (
    <div className="App">
      <Toaster 
        position="bottom-right" 
        toastOptions={{
          style: {
            background: '#052B3F',
            color: '#fff',
            border: '1px solid rgba(0, 206, 209, 0.3)'
          }
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PortfolioPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
